import { useState } from "react";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { isApiClientError } from "@/shared/api/errors";
import { useAuth } from "@/shared/auth/AuthContext";
import {
  Banner,
  EntityFormDialog,
  FormField,
  Input,
  useToast,
} from "@/shared/ui";

export type ChangePasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: ChangePasswordDialogProps) {
  const { user, http } = useAuth();
  const { toast } = useToast();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setError(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const handleSave = async () => {
    if (!currentPassword.trim()) {
      setError("Vui lòng nhập mật khẩu hiện tại");
      return;
    }
    if (newPassword.length < 8) {
      setError("Mật khẩu mới phải có tối thiểu 8 ký tự");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Mật khẩu xác nhận không khớp");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await http.changeMyPassword({
        currentPassword,
        newPassword,
      });

      handleOpenChange(false);
      toast({
        tone: "success",
        title: "Đổi mật khẩu thành công",
        message: "Mật khẩu của bạn đã được thay đổi thành công.",
      });
    } catch (e) {
      const msg = isApiClientError(e)
        ? e.message
        : "Không đổi được mật khẩu. Vui lòng kiểm tra lại mật khẩu hiện tại.";
      setError(msg);
      toast({
        tone: "error",
        title: "Lỗi thay đổi mật khẩu",
        message: msg,
      });
    } finally {
      setSaving(false);
    }
  };

  const dirty = Boolean(currentPassword || newPassword || confirmPassword);

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Thay đổi mật khẩu"
      icon={<KeyRound className="size-4" />}
      description={user?.username ? `@${user.username}` : undefined}
      size="sm"
      dirty={dirty}
      saving={saving}
      primaryLabel="Đổi mật khẩu"
      cancelLabel="Hủy"
      onPrimary={() => void handleSave()}
    >
      <div className="space-y-4 pt-1">
        {error ? <Banner tone="danger" title={error} /> : null}

        <FormField label="Mật khẩu hiện tại" required>
          <Input
            type={showCurrent ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Nhập mật khẩu hiện tại"
            autoComplete="current-password"
            size="md"
            endAdornment={
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowCurrent(!showCurrent)}
                className="text-text-muted hover:text-text cursor-pointer focus:outline-none"
                aria-label={showCurrent ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              >
                {showCurrent ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            }
          />
        </FormField>

        <FormField
          label="Mật khẩu mới"
          hint="Tối thiểu 8 ký tự"
          required
        >
          <Input
            type={showNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Nhập mật khẩu mới"
            autoComplete="new-password"
            size="md"
            endAdornment={
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowNew(!showNew)}
                className="text-text-muted hover:text-text cursor-pointer focus:outline-none"
                aria-label={showNew ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              >
                {showNew ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            }
          />
        </FormField>

        <FormField label="Xác nhận mật khẩu mới" required>
          <Input
            type={showConfirm ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Nhập lại mật khẩu mới"
            autoComplete="new-password"
            size="md"
            endAdornment={
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirm(!showConfirm)}
                className="text-text-muted hover:text-text cursor-pointer focus:outline-none"
                aria-label={showConfirm ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              >
                {showConfirm ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            }
          />
        </FormField>
      </div>
    </EntityFormDialog>
  );
}
